import { useCallback, useEffect, useRef, useState } from 'react'
import type { FieldPath, UseFormReturn } from 'react-hook-form'
import { showNotification } from '@mantine/notifications'
import type { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import type { ProgressNoteEditFormValues } from '../progressNoteEditSchema'
import { replaceMentions } from './replaceMentions'
import { useSpeechCapture } from './useSpeechCapture'
import {
  readDictationSelection,
  resolveDictationField,
  type DictationField,
  type DictationSelection,
} from './dictationFields'

export type DictateMode = 'field' | 'replacement' | 'organize'
export type DictatePhase = 'idle' | 'recording' | 'paused' | 'processing' | 'preview' | 'error'

type OrpcClient = ReturnType<typeof useClient>
type FormatResult = Awaited<ReturnType<OrpcClient['scribeDictateFormat']>>
/** One dictated medication plus its provider-review state (PD-1516). */
export type MedReview = FormatResult['medications'][number] & { resolved: boolean }

type FormPath = FieldPath<ProgressNoteEditFormValues>

/** Anything that is part of the dictate UI itself — pressing it must not clear the tracked field. */
const DICTATE_CONTROLS_SELECTOR =
  '[data-testid^="ai-dictate"],[data-testid^="dictate-mic"],[data-testid="progress-note-ai-dictate"]'

/**
 * PD-1516 "AI Dictate" — one adaptive control, three flows resolved from
 * context at record start: a focused narrative field records into that field;
 * a selection inside it starts a replacement; neither (on a manual note)
 * records a whole-note dictation handed to `onOrganizeDictation`, which runs
 * it through the full draft pipeline. All field/replacement output is
 * preview-only until the provider accepts, and every medication the AI is not
 * certain of must be confirmed or changed before Accept enables.
 */
export function useAiDictate(args: {
  enabled: boolean
  isCreateMode: boolean
  encounterId: string | undefined
  methods: UseFormReturn<ProgressNoteEditFormValues>
  client: OrpcClient
  /**
   * Whole-note dictation handoff — owns progress/apply/toasts; resolves true
   * when applied. Must consult `isStale` once generation resolves and discard
   * the result if true: the provider started a newer dictation meanwhile, and
   * stale content must never reach the disclaimer or the form.
   */
  onOrganizeDictation?: (dictation: string, isStale: () => boolean) => Promise<boolean>
}) {
  const { isCreateMode, encounterId, methods, client, onOrganizeDictation } = args
  // A mic without an encounter dead-ends at Finish, and a live scribe session
  // owns the microphone — both gate every entry point, not just the button.
  const enabled = args.enabled && Boolean(encounterId)
  const speech = useSpeechCapture()

  const [phase, setPhase] = useState<DictatePhase>('idle')
  const [mode, setMode] = useState<DictateMode | null>(null)
  const [target, setTarget] = useState<DictationField | null>(null)
  const [selection, setSelection] = useState<DictationSelection | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [medReviews, setMedReviews] = useState<MedReview[]>([])

  // Autosave/save guards read this synchronously between renders.
  const activeRef = useRef(false)
  activeRef.current = phase !== 'idle'

  // The engine pauses itself on a non-routine error (PD-1582); mirror it so the
  // bar offers Resume for the retry instead of a Pause that would no-op.
  useEffect(() => {
    if (speech.status === 'paused' && phase === 'recording') setPhase('paused')
  }, [speech.status, phase])

  // Bumped on start, cancel, and each Finish: a continuation resuming after a
  // cancel (or a double-clicked Finish) must drop its result, not resurrect
  // discarded text as a preview.
  const runSeqRef = useRef(0)

  const hpiRowIdRef = useRef<string | null>(null)
  // The captured dictation survives a failed formatting call so Retry never
  // asks the provider to speak it again.
  const pendingDictationRef = useRef<string | null>(null)

  // The DOM element being dictated into — outlined while a run is active so
  // the destination stays obvious after the browser focus is lost.
  const targetElementRef = useRef<HTMLElement | null>(null)
  const setTargetOutline = useCallback((element: Element | null) => {
    const previous = targetElementRef.current
    if (previous) {
      previous.style.outline = ''
      previous.style.outlineOffset = ''
    }
    targetElementRef.current = element instanceof HTMLElement ? element : null
    if (targetElementRef.current) {
      targetElementRef.current.style.outline = '2px solid var(--mantine-color-violet-5)'
      targetElementRef.current.style.outlineOffset = '2px'
    }
  }, [])
  useEffect(() => () => setTargetOutline(null), [setTargetOutline])

  // Last focused dictation-capable field, via delegation — clicking the
  // toolbar button blurs the field, so focus must be tracked ahead of time.
  // A pointerdown anywhere OUTSIDE a dictation field or the dictate controls
  // clears it: without that, dictation follows a field the provider had
  // visibly left, and whole-note mode is unreachable.
  const lastFocusRef = useRef<{ field: DictationField; element: Element } | null>(null)
  useEffect(() => {
    if (!enabled) return
    const onFocusIn = (event: FocusEvent) => {
      const field = resolveDictationField(event.target as Element)
      if (field) lastFocusRef.current = { field, element: event.target as Element }
    }
    const onPointerDown = (event: PointerEvent) => {
      const element = event.target instanceof Element ? event.target : null
      if (!element) return
      if (element.closest(DICTATE_CONTROLS_SELECTOR)) return
      if (!resolveDictationField(element)) lastFocusRef.current = null
    }
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [enabled])

  const reset = useCallback(() => {
    hpiRowIdRef.current = null
    pendingDictationRef.current = null
    setTargetOutline(null)
    setPhase('idle')
    setMode(null)
    setTarget(null)
    setSelection(null)
    setPreview(null)
    setMedReviews([])
  }, [setTargetOutline])

  const start = useCallback(
    (explicit?: { field: DictationField; element: Element } | 'organize') => {
      if (activeRef.current || !enabled) return
      const focused = explicit === 'organize' ? null : (explicit ?? lastFocusRef.current ?? null)
      if (focused) {
        const sel = readDictationSelection(focused.element)
        // HPI rows are addressed by index, and rows can be removed while the
        // preview is open — capture the row's stable data id to re-resolve at
        // accept time instead of trusting the frozen index.
        const hpiMatch = /^summary\.hpi\.(\d+)\.text$/.exec(focused.field.path)
        const hpiRow = hpiMatch
          ? methods.getValues(`summary.hpi.${Number(hpiMatch[1])}` as FormPath)
          : undefined
        hpiRowIdRef.current = (hpiRow as { id?: string } | undefined)?.id ?? null
        setTarget(focused.field)
        setSelection(sel)
        setMode(sel ? 'replacement' : 'field')
        setTargetOutline(focused.element)
      } else if (isCreateMode) {
        setTarget(null)
        setSelection(null)
        setMode('organize')
      } else {
        showNotification({
          color: 'blue',
          message: 'Click into a note field first — AI Dictate records into the active field.',
        })
        return
      }
      runSeqRef.current += 1
      setPreview(null)
      setMedReviews([])
      speech.start()
      setPhase('recording')
    },
    [isCreateMode, speech, enabled, methods, setTargetOutline],
  )

  const pause = useCallback(() => {
    speech.pause()
    setPhase('paused')
  }, [speech])

  const resume = useCallback(() => {
    speech.resume()
    setPhase('recording')
  }, [speech])

  const cancel = useCallback(() => {
    runSeqRef.current += 1
    speech.cancel()
    reset()
  }, [speech, reset])

  const runFormat = useCallback(
    async (dictation: string, run: number) => {
      if (!encounterId) return
      setPhase('processing')
      try {
        const result = await client.scribeDictateFormat({
          encounterId,
          dictation,
          mode: mode === 'replacement' ? 'replacement' : 'field',
          ...(target ? { fieldLabel: target.label } : null),
          ...(selection ? { selectedText: selection.text } : null),
        })
        if (runSeqRef.current !== run) return
        pendingDictationRef.current = null
        setPreview(result.text)
        setMedReviews(result.medications.map((med) => ({ ...med, resolved: med.confident })))
        setPhase('preview')
      } catch (error) {
        if (runSeqRef.current !== run) return
        logError('ai-dictate', 'Dictation formatting failed', error)
        // The speech is already captured — never make the provider redo it.
        pendingDictationRef.current = dictation
        setPhase('error')
      }
    },
    [client, encounterId, mode, target, selection],
  )

  const runOrganize = useCallback(
    async (dictation: string, run: number) => {
      // Whole-note dictation runs the full draft pipeline; the caller owns
      // progress, the review disclaimer, apply, and its own failure toasts.
      // On failure the bar re-arms with Retry and the speech KEPT — the
      // provider must never have to dictate the note again.
      reset()
      // The reset above drops the bar to idle for the whole call, so the
      // provider can start a new dictation while this one is still running —
      // the caller drops a stale result before applying it, and a late
      // failure must not re-arm over the new run.
      const applied =
        (await onOrganizeDictation?.(dictation, () => runSeqRef.current !== run)) ?? false
      if (runSeqRef.current !== run) return
      if (!applied) {
        pendingDictationRef.current = dictation
        setMode('organize')
        setPhase('error')
      }
    },
    [reset, onOrganizeDictation],
  )

  const finishRecording = useCallback(async () => {
    const run = ++runSeqRef.current
    const dictation = (await speech.finish()).trim()
    if (runSeqRef.current !== run) return
    if (!dictation) {
      showNotification({ color: 'yellow', message: 'No speech was captured.' })
      reset()
      return
    }
    if (!encounterId) {
      showNotification({ color: 'red', message: 'No visit is linked to this note.' })
      reset()
      return
    }
    if (mode === 'organize') {
      await runOrganize(dictation, run)
      return
    }
    await runFormat(dictation, run)
  }, [speech, reset, encounterId, mode, runOrganize, runFormat])

  const retryFormat = useCallback(() => {
    const dictation = pendingDictationRef.current
    if (!dictation) return
    if (mode === 'organize') {
      void runOrganize(dictation, ++runSeqRef.current)
      return
    }
    void runFormat(dictation, ++runSeqRef.current)
  }, [mode, runOrganize, runFormat])

  const confirmMedication = useCallback((index: number) => {
    setMedReviews((prev) => prev.map((m, i) => (i === index ? { ...m, resolved: true } : m)))
  }, [])

  /**
   * Swap the proposed medication text in the editable preview and resolve it.
   * Resolves ONLY when the swap actually landed: marking a correction resolved
   * while the preview still carries the misheard token would let it into the
   * note. The provider may have edited the phrase away — fall back to the
   * dictated original, and otherwise ask them to edit the preview directly.
   * Every mention is swapped, except one nested in another medication's
   * phrase — that text is the sibling's, not this item's.
   */
  const changeMedication = useCallback(
    (index: number, replacement: string) => {
      const text = replacement.trim()
      if (!text) return
      const med = medReviews[index]
      if (!med) return
      const mentions = (phrase: string) => preview?.toLowerCase().includes(phrase.toLowerCase())
      const target = mentions(med.normalized)
        ? med.normalized
        : mentions(med.original)
          ? med.original
          : null
      if (!target) {
        showNotification({
          color: 'yellow',
          message: `Couldn't find "${med.normalized}" in the text — edit the preview directly, then Confirm.`,
        })
        return
      }
      const siblings = medReviews.flatMap((m, i) => (i === index ? [] : [m.normalized, m.original]))
      const nextPreview = replaceMentions(preview ?? '', target, text, siblings)
      if (nextPreview === preview) {
        showNotification({
          color: 'yellow',
          message: `Couldn't replace "${med.normalized}" without changing another medication — edit the preview directly, then Confirm.`,
        })
        return
      }
      setPreview(nextPreview)
      setMedReviews((prev) =>
        prev.map((m, i) => (i === index ? { ...m, normalized: text, resolved: true } : m)),
      )
    },
    [medReviews, preview],
  )

  const acceptPreview = useCallback(() => {
    if (!preview || !target) return
    if (medReviews.some((m) => !m.resolved)) return
    let path = target.path as FormPath
    if (hpiRowIdRef.current) {
      const rows = (methods.getValues('summary.hpi') ?? []) as { id: string }[]
      const idx = rows.findIndex((row) => row.id === hpiRowIdRef.current)
      if (idx === -1) {
        showNotification({
          color: 'red',
          message: 'That HPI paragraph was removed — dictate into another field.',
        })
        reset()
        return
      }
      path = `summary.hpi.${idx}.text` as FormPath
    }
    const current = ((methods.getValues(path) as string | undefined) ?? '').toString()
    if (mode === 'replacement' && selection) {
      // The form is editable during preview — refuse a splice the text moved
      // out from under rather than corrupt the field.
      if (current.slice(selection.start, selection.end) !== selection.text) {
        showNotification({
          color: 'red',
          message: 'The selected text changed — place the cursor and dictate again.',
        })
        reset()
        return
      }
      const next = current.slice(0, selection.start) + preview + current.slice(selection.end)
      methods.setValue(path, next as never, { shouldDirty: true, shouldTouch: true })
    } else {
      const next = current.trim() ? `${current.replace(/\s+$/, '')} ${preview}` : preview
      methods.setValue(path, next as never, { shouldDirty: true, shouldTouch: true })
    }
    reset()
  }, [preview, target, mode, selection, methods, reset, medReviews])

  return {
    supported: speech.supported,
    phase,
    mode,
    target,
    selection,
    liveText: speech.liveText,
    speechError: speech.error,
    preview,
    setPreview,
    medReviews,
    confirmMedication,
    changeMedication,
    activeRef,
    start,
    pause,
    resume,
    cancel,
    finishRecording,
    retryFormat,
    acceptPreview,
    discardPreview: reset,
    enabled,
  }
}

export type AiDictate = ReturnType<typeof useAiDictate>
